import { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import styles from './index.module.sass';
import repo from 'service/repository';
import amplitude from 'service/amplitude';
import iconHint from 'images/icon-hint.png';

const cx = classnames.bind(styles);

/**
 * ContactModal Component
 *
 * A modal component that collects and submits contact info (email, phone) for charging notifications.
 * The mask cannot be clicked to close the modal - only the button can close it.
 *
 * @param {Object} props
 * @param {Function} [props.onSubmitForm] - Callback invoked after a successful form submit. No arguments.
 * @param {boolean} props.isOpen - Whether the modal is visible.
 * @param {Object} [props.data] - Prefill and record data.
 * @param {string} [props.data.id] - Charging record ID for update.
 * @param {string} [props.data.email] - Prefill email (ignored if empty, 'null', or '<nil>').
 * @param {string} [props.data.userPhone] - Prefill phone (ignored if empty, 'null', or '<nil>').
 */

const ContactModal = ({ onSubmitForm = () => {}, isOpen, data }) => {
  const toInitialValue = val => (val && val !== '<nil>' && val !== 'null' ? val : '');

  const [email, setEmail] = useState(() => toInitialValue(data?.email));
  const [phone, setPhone] = useState(() => toInitialValue(data?.userPhone));
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [hasSubmitError, setHasSubmitError] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    const shouldHideScroll = isOpen && (!isFormSubmitted || hasSubmitError);

    if (shouldHideScroll) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen, isFormSubmitted, hasSubmitError]);

  useEffect(() => {
    if (isOpen && !isFormSubmitted && !isLoading) {
      const initialEmail = toInitialValue(data?.email);
      const initialPhone = toInitialValue(data?.userPhone);

      if (initialEmail) {
        setEmail(initialEmail);
      }

      if (initialPhone) {
        setPhone(initialPhone);
      }
    }
  }, [isOpen, isFormSubmitted, isLoading, data]);

  useEffect(() => {
    if (isOpen) {
      amplitude.chargingContactInputPageView({ phone, email });
    }
  }, [isOpen]); //eslint-disable-line

  const validateEmail = email => {
    // email is required
    if (!email || email.trim() === '') {
      return '請輸入電子郵件';
    }

    // email format validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return '請輸入有效的電子郵件地址';
    }

    return '';
  };

  const validatePhone = phone => {
    // phone is optional
    if (!phone || phone.trim() === '') {
      return '';
    }

    // check if the phone only contains numbers
    if (!/^\d+$/.test(phone)) {
      return '手機號碼只能包含數字';
    }

    // check if the phone is 10 digits and starts with 09
    if (!/^09\d{8}$/.test(phone)) {
      return '請輸入有效的手機號碼';
    }

    return '';
  };

  const updateContactInfo = async formData => {
    setIsLoading(true);
    const [error] = await repo.updateChargingRecord(formData);
    if (error) {
      return error;
    }

    setHasSubmitError(false);
    setIsLoading(false);

    return;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    setEmailError('');
    setPhoneError('');

    const emailValue = email?.trim();
    const phoneValue = phone?.trim();

    // validate
    const emailValidationError = validateEmail(emailValue);
    const phoneValidationError = validatePhone(phoneValue);

    if (emailValidationError) {
      setEmailError(emailValidationError);
    }

    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
    }

    if (emailValidationError || phoneValidationError) {
      return;
    }

    const formData = {
      id: data?.id,
      email: emailValue,
      phone: phoneValue,
    };

    setIsLoading(true);
    const error = await updateContactInfo(formData);
    amplitude.chargingContactInputSendOnClick({ phone: phoneValue, email: emailValue });

    setIsFormSubmitted(true);
    setIsLoading(false);

    if (error) {
      setHasSubmitError(true);
      return;
    }

    onSubmitForm?.();
  };

  if (!isOpen) {
    return null;
  }

  if (hasSubmitError) {
    return (
      <SubmitErrorModal
        isOpen={hasSubmitError}
        onResendButtonClick={() => {
          updateContactInfo({ id: data?.id, email, phone });
          amplitude.chargingContactInputResendButtonOnClick();
        }}
        onClose={() => {
          setHasSubmitError(false);
        }}
      />
    );
  }

  return (
    <div className={cx({ mask: true, show: isOpen && !isFormSubmitted })}>
      <div className={styles.modal}>
        <div className={styles.content}>
          <form onSubmit={handleSubmit} noValidate>
            <h1 className={styles.title}>輸入聯繫資訊</h1>
            <p className={styles.desc}>當停止充電或發生異狀時，我們會立即通知您。</p>

            <div className={styles.inputs}>
              <input
                className={styles.input}
                type="tel"
                name="phone"
                placeholder="輸入手機號碼"
                value={phone}
                onChange={e => setPhone(e.target.value)}
              />
              <span className={styles.error}>{phoneError}</span>
              <input
                className={styles.input}
                type="email"
                name="email"
                placeholder="輸入電子郵件"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              <span className={styles.error}>{emailError}</span>
            </div>
            <button className={styles['confirm-button']} type="submit" disabled={isLoading}>
              {isLoading ? '寄送通知中...' : '寄送通知'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const SubmitErrorModal = ({ isOpen, onResendButtonClick, onClose }) => {
  const [closeModal, setCloseModal] = useState(false);
  if (!isOpen || closeModal) {
    return null;
  }

  return (
    <div className={cx({ mask: true, show: isOpen && !closeModal }, styles['submit-error-modal'])}>
      <div className={styles.modal}>
        <div className={styles.content}>
          <button
            className={styles['icon-close-button']}
            onClick={() => {
              onClose?.();
              setCloseModal(true);
              amplitude.chargingContactInputIconCloseButtonOnClick();
            }}
          >
            x
          </button>
          <img src={iconHint} alt="hint" />
          <h1 className={styles.title}>寄送失敗</h1>
          <p className={styles.desc}>
            系統連線不穩定，請移動至收訊良好處重試。 若仍失敗，請保持網頁開啟以追蹤充電動態。
          </p>
          <div className={styles.buttons}>
            <button className={styles['submit-button']} type="button" onClick={() => onResendButtonClick?.()}>
              重新送出
            </button>
            <button
              className={styles['close-button']}
              type="button"
              onClick={() => {
                onClose?.();
                setCloseModal(true);
                amplitude.chargingContactInputCloseButtonOnClick();
              }}
            >
              關閉視窗
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
